import {
  localDateFromEpoch,
  materialiseSession,
  buildAddedSet,
  transitionSession,
  selectPreviousPerformance,
  prefillForSet,
  detectPersonalRecords,
  suggestProgression,
  strengthVolume,
  summariseCardio,
  type CardioSegment,
  type CardioSummary,
  type LoggedSet,
  type PerformedExercise,
  type PreviousPerformance,
  type ProgressionSuggestion,
  type SessionDraft,
  type SessionStatus,
} from '@liftx/core';
import { db, type LiftXDatabase } from '../db';
import { isLive, tombstoneById, tombstoneRows } from '../tombstone';
import { createIntegrityRepository } from './integrityRepository';
import { createExerciseRepository } from './exerciseRepository';
import { createPlanRepository } from './planRepository';
import { createPersonalRecordsRepository } from './personalRecordsRepository';
import { progressRepository } from './progressRepository';
import type {
  ActiveHoldTimer,
  ActiveRestTimer,
  CardioSegmentRecord,
  PersonalRecordRecord,
  SessionBlockRecord,
  SessionExerciseRecord,
  SessionRecord,
  SessionSetRecord,
} from '../types';

/**
 * `cardioSegments`'s DB row shape predates and doesn't line up field-for-field
 * with `@liftx/core`'s `CardioSegment` (`avgHeartRate` vs. `avgHr`, no
 * `calories`/`maxHr` columns) — this maps what the row actually has onto what
 * `detectPersonalRecords`/`cardio.ts` expect, rather than casting past the
 * mismatch. `inclinePct`/`resistanceLevel` *do* have real columns (Treadmill
 * Incline feature; Level → estimated speed feature) and round-trip below.
 */
function toCardioSegment(row: CardioSegmentRecord): CardioSegment {
  return {
    id: row.id,
    sessionExerciseId: row.sessionExerciseId,
    segmentIndex: row.orderIndex,
    durationS: row.durationS,
    distanceM: row.distanceM,
    calories: null,
    avgHr: row.avgHeartRate,
    maxHr: null,
    inclinePct: row.inclinePct ?? null,
    resistanceLevel: row.resistanceLevel ?? null,
    isCompleted: row.isCompleted,
  };
}

/**
 * Starting, running, and finishing a workout. Master Phase 6.
 *
 * As with Phase 5's `planRepository`, essentially no domain logic lives here
 * — `@liftx/core`'s `materialise.ts`/`session-state.ts`/`rest-timer.ts`/
 * `previous-performance.ts`/`personal-records.ts`/`progression.ts` already
 * implement every rule this file needs. This is the persistence layer over
 * code that already existed, reshaping stored rows into the shapes those
 * pure functions expect and back again.
 *
 * **The one rule that matters more than any other in this file:** every
 * meaningful action is a direct, immediate write. There is no buffering, no
 * "save on exit". `PHASE-0-ARCHITECTURE.md`'s own risk register (R-5) states
 * it as the mitigation for the highest-consequence risk in the whole app:
 * "Sets persisted on completion, never buffered."
 */

export interface StartFromPlanDayOptions {
  name?: string;
  tzOffsetMin?: number;
  localDate?: string;
}

export interface CompleteSetInput {
  weightG?: number | null;
  bodyweightG?: number | null;
  addedWeightG?: number | null;
  assistWeightG?: number | null;
  reps?: number | null;
  durationS?: number | null;
  rpe?: number | null;
}

/**
 * The loggable content of one cardio segment — Cardio Phase C2, plus
 * `resistanceLevel`/`estimatedSpeedKph` from the Level → estimated speed
 * feature and `inclinePct` from the Treadmill Incline feature. `@liftx/core`'s
 * `CardioSegment` additionally carries `calories` and `maxHr`, which this row
 * has never had and which `toCardioSegment()` above fills with `null`; C2
 * deliberately does not add columns for them.
 *
 * ACTUAL pace and speed are absent by design, not oversight: they are
 * derived from duration and distance (`cardio.ts`) and storing them would
 * create a second source of truth that drifts the moment either input is
 * edited. `estimatedSpeedKph` is not that — it is a genuinely separate,
 * independently-editable estimate/target (own doc on `CardioSegmentRecord`).
 */
export interface CardioSegmentInput {
  durationS?: number | null;
  distanceM?: number | null;
  avgHeartRate?: number | null;
  /** The machine's own Level reading — Level → estimated speed feature. */
  resistanceLevel?: number | null;
  /** The athlete's current best estimate of actual speed, in km/h — see `CardioSegmentRecord.estimatedSpeedKph`. */
  estimatedSpeedKph?: number | null;
  /** Treadmill incline, as a percentage grade — Treadmill Incline feature. A genuinely separate concept from `resistanceLevel`, never reusing its field or meaning. */
  inclinePct?: number | null;
}

export interface WorkoutStats {
  totalVolumeGramReps: number;
  totalSets: number;
  totalReps: number;
  durationS: number;
  /**
   * Cardio totals for the whole session — Cardio Phase C6.
   *
   * `null` when the session contains no cardio segments at all, so a
   * strength-only workout is byte-identical to what it was before C6 and
   * every caller can simply not render the block.
   *
   * Computed by `summariseCardio`, which counts ONLY completed segments.
   * That is the invariant that makes this honest: C3 seeds a new segment
   * with the prescribed duration and distance, so an untouched segment
   * carries numbers the athlete has not performed. They must never reach a
   * total. `isCompleted` is the proof of performance, here as everywhere.
   *
   * Derived on read, never stored — no schema change, no migration.
   */
  cardio: CardioSummary | null;
}

export interface ProgressionSuggestionEntry {
  sessionExerciseId: string;
  exerciseId: string;
  suggestion: ProgressionSuggestion;
}

export interface WorkoutSummary {
  session: SessionRecord;
  stats: WorkoutStats;
  achievedRecords: PersonalRecordRecord[];
  progressionSuggestions: ProgressionSuggestionEntry[];
}

export class InvalidSessionOperationError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'InvalidSessionOperationError';
  }
}

/** Local timezone offset in `@liftx/core`'s convention — the sign JS's own `getTimezoneOffset()` inverts. */
function currentTzOffsetMin(): number {
  return -new Date().getTimezoneOffset();
}

export interface SessionRepository {
  startFromPlanDay(planDayId: string, options?: StartFromPlanDayOptions): Promise<SessionRecord>;
  startFree(name?: string | null): Promise<SessionRecord>;
  getOpenSession(): Promise<SessionRecord | undefined>;
  getSession(id: string): Promise<SessionRecord | undefined>;
  pause(id: string): Promise<SessionRecord>;
  resume(id: string): Promise<SessionRecord>;
  abandon(id: string): Promise<SessionRecord>;
  complete(id: string): Promise<WorkoutSummary>;
  /**
   * Fully recomputed from persisted data alone — safe to call any time after
   * completion, including after a reload, unlike `complete()`'s return value
   * (which nothing should rely on surviving navigation).
   */
  getSummary(id: string): Promise<WorkoutSummary>;
  deleteSession(id: string): Promise<void>;

  listBlocks(sessionId: string): Promise<SessionBlockRecord[]>;
  listExercises(sessionBlockId: string): Promise<SessionExerciseRecord[]>;
  listSets(sessionExerciseId: string): Promise<SessionSetRecord[]>;
  listSegments(sessionExerciseId: string): Promise<CardioSegmentRecord[]>;

  completeSet(setId: string, values: CompleteSetInput): Promise<SessionSetRecord>;
  uncompleteSet(setId: string): Promise<SessionSetRecord>;
  addSet(sessionExerciseId: string): Promise<SessionSetRecord>;
  removeSet(setId: string): Promise<void>;

  // ── Cardio segments (Cardio Phase C2) ──────────────────────────────────
  // The exact counterparts of the four set mutators above, deliberately
  // named and shaped to match them rather than forming a second lifecycle.
  // `listSegments` already existed; these are what make it useful.
  /** Appends a segment. `orderIndex` is max+1 over live siblings, as `addExercise` does. */
  addSegment(sessionExerciseId: string, values?: CardioSegmentInput): Promise<CardioSegmentRecord>;
  /** Edits a segment's logged values without touching its identity or completion state. */
  updateSegment(segmentId: string, values: CardioSegmentInput): Promise<CardioSegmentRecord>;
  /** Marks complete, optionally writing final values in the same call — mirrors `completeSet`. */
  completeSegment(segmentId: string, values?: CardioSegmentInput): Promise<CardioSegmentRecord>;
  uncompleteSegment(segmentId: string): Promise<CardioSegmentRecord>;
  /** Tombstones, never hard-deletes — same contract as `removeSet`. */
  removeSegment(segmentId: string): Promise<void>;
  addExercise(sessionBlockId: string, exerciseId: string): Promise<SessionExerciseRecord>;
  /**
   * Removes an exercise from THIS session only: tombstones the exercise and
   * every child set and cardio segment in one transaction (the same cascade
   * `deleteSession` uses, scoped to one exercise). Never touches the plan,
   * the exercise library, or the containing block — a free workout needs its
   * block to survive for "Add exercise". Clears an active Hold countdown that
   * belongs to one of the removed sets. Rejects a completed session; removing
   * an already-removed exercise is a silent no-op, as `removeSet` is.
   */
  removeExercise(sessionExerciseId: string): Promise<void>;
  /**
   * @deprecated No UI calls this any more — "Skip exercise" was replaced by
   * `removeExercise`. Kept only so existing/legacy-fixture tests can still
   * write a `'skipped'` row; the History/Summary phase will retire it.
   */
  skipExercise(sessionExerciseId: string): Promise<SessionExerciseRecord>;
  /** @deprecated See `skipExercise`. */
  unskipExercise(sessionExerciseId: string): Promise<SessionExerciseRecord>;
  updateSessionNotes(sessionId: string, notes: string | null): Promise<void>;
  updateSetNotes(setId: string, notes: string | null): Promise<void>;

  startRestTimer(sessionId: string, sessionBlockId: string, durationS: number): Promise<ActiveRestTimer>;
  adjustRestTimer(sessionId: string, deltaS: number): Promise<ActiveRestTimer | null>;
  skipRestTimer(sessionId: string): Promise<void>;

  startHoldTimer(sessionId: string, sessionSetId: string, durationS: number): Promise<ActiveHoldTimer>;
  clearHoldTimer(sessionId: string): Promise<void>;

  getPreviousPerformance(exerciseId: string, excludeSessionId: string): Promise<PreviousPerformance | null>;
  prefillFor(previous: PreviousPerformance | null, setIndex: number): ReturnType<typeof prefillForSet>;
}

export function createSessionRepository(database: LiftXDatabase = db): SessionRepository {
  const integrityRepository = createIntegrityRepository(database);
  const exerciseRepository = createExerciseRepository(database);
  const planRepository = createPlanRepository(database);
  const personalRecordsRepository = createPersonalRecordsRepository(database);

  const allSessionTables = [
    database.sessions,
    database.sessionBlocks,
    database.sessionExercises,
    database.sessionSets,
  ] as const;

  /**
   * A `completed` session is terminal (`session-state.ts`) — corrections go
   * through the audited history-edit path (Master Phase 7's
   * `historyEditRepository`), not by reopening the live mutators below. An
   * `abandoned` session stays fully live-editable once `resume()`d back to
   * `active`, so it is deliberately not guarded here.
   */
  async function assertSessionMutable(sessionId: string): Promise<void> {
    const session = await database.sessions.get(sessionId);
    if (session?.status === 'completed') {
      throw new InvalidSessionOperationError(
        `Session ${sessionId} is completed — corrections go through the audited history-edit path, not by reopening the session.`,
      );
    }
  }

  /** Same guard, resolved from a `sessionExerciseId` — the common case for set-level mutators. */
  async function assertSessionExerciseMutable(sessionExerciseId: string): Promise<void> {
    const exercise = await database.sessionExercises.get(sessionExerciseId);
    if (exercise === undefined) return; // the caller's own existence check reports this
    await assertSessionMutable(exercise.sessionId);
  }

  /**
   * Cardio Phase C8 — closes a pre-existing gap noted (not fixed) in C7:
   * `progressRepository`'s trend cache had no call to `invalidate()` from
   * anywhere in production code, so a set or segment logged after that
   * exercise's trend had already been viewed once this session would not
   * appear until the cache happened to be cleared some other way.
   * `progressRepository.getExerciseTrend`/`getCardioTrend` include
   * non-completed sessions too (only `isLive`, never `status`), so this
   * fires from every live mutator below, not only from `complete()`.
   */
  async function invalidateProgressFor(sessionExerciseId: string): Promise<void> {
    const exercise = await database.sessionExercises.get(sessionExerciseId);
    if (exercise?.exerciseId != null) progressRepository.invalidate(exercise.exerciseId);
  }

  /** Writes a full `materialiseSession()` draft as one transaction. */
  async function persistDraft(draft: SessionDraft, name: string | null): Promise<SessionRecord> {
    // `database.exercises` is read (not written) inside this transaction for
    // `exerciseNameSnapshot` — Dexie requires every table a transaction
    // touches, read or write, to be declared up front.
    //
    // `cardioSegments` is declared explicitly for the same reason: it is not
    // part of `allSessionTables`, and since C3 a cardio exercise writes its
    // segments here. Real IndexedDB throws on an undeclared store, so omitting
    // it would fail only against a real browser, not the fake used in tests.
    return await database.transaction(
      'rw',
      [...allSessionTables, database.exercises, database.cardioSegments],
      async () => {
      const record: SessionRecord = {
        id: draft.id,
        name,
        status: draft.status,
        startedAt: draft.startedAt,
        endedAt: draft.endedAt,
        localDate: draft.localDate,
        tzOffsetMin: draft.tzOffsetMin,
        planId: draft.planId,
        planDayId: draft.planDayId,
        planSnapshot: draft.planId === null ? null : draft,
        notes: null,
        activeRestTimer: null,
        activeHoldTimer: null,
      };
      await database.sessions.add(record);

      for (const block of draft.blocks) {
        const blockRecord: SessionBlockRecord = {
          id: block.id,
          sessionId: block.sessionId,
          orderIndex: block.orderIndex,
          kind: block.kind,
          restSeconds: block.restSeconds,
          sourcePlanBlockId: block.sourcePlanBlockId,
        };
        await database.sessionBlocks.add(blockRecord);

        for (const exercise of block.exercises) {
          const exerciseRow = await exerciseRepository.get(exercise.exerciseId);
          const exerciseRecord: SessionExerciseRecord = {
            id: exercise.id,
            sessionBlockId: exercise.sessionBlockId,
            sessionId: block.sessionId,
            exerciseId: exercise.exerciseId,
            exerciseNameSnapshot: exerciseRow?.name ?? exercise.exerciseId,
            mediaIdsSnapshot: exerciseRow !== undefined && exerciseRow.mediaIds.length > 0 ? exerciseRow.mediaIds : null,
            orderIndex: exercise.orderIndex,
            status: exercise.status,
            modality: exercise.modality,
            loadType: exercise.loadType,
            restSeconds: exercise.restSeconds,
            notes: null,
            sourcePlanExerciseId: exercise.sourcePlanExerciseId,
            prescriptionSnapshot: exercise.prescribed,
          };
          await database.sessionExercises.add(exerciseRecord);

          const setRecords: SessionSetRecord[] = exercise.sets.map((set) => ({
            id: set.id,
            sessionExerciseId: set.sessionExerciseId,
            setIndex: set.setIndex,
            dropIndex: set.dropIndex,
            setType: set.setType,
            parentSetId: set.parentSetId,
            loadType: set.loadType,
            weightG: set.weightG,
            bodyweightG: set.bodyweightG,
            bodyweightSource: set.bodyweightSource,
            addedWeightG: set.addedWeightG,
            assistWeightG: set.assistWeightG,
            reps: set.reps,
            durationS: set.durationS,
            rpe: set.rpe,
            isCompleted: set.isCompleted,
            completedAt: set.completedAt,
            notes: null,
          }));
          if (setRecords.length > 0) await database.sessionSets.bulkAdd(setRecords);

          // Cardio Phase C3. Populated only for `modality: 'cardio'`, and for
          // those exercises `setRecords` above is empty — the two are mutually
          // exclusive by construction in `materialiseSession`, never both.
          const segmentRecords: CardioSegmentRecord[] = exercise.segments.map((segment) => ({
            id: segment.id,
            sessionExerciseId: segment.sessionExerciseId,
            orderIndex: segment.orderIndex,
            distanceM: segment.distanceM,
            durationS: segment.durationS,
            avgHeartRate: segment.avgHeartRate,
            isCompleted: segment.isCompleted,
          }));
          if (segmentRecords.length > 0) await database.cardioSegments.bulkAdd(segmentRecords);
        }
      }

      return record;
    });
  }

  async function requireSession(id: string): Promise<SessionRecord> {
    const session = await database.sessions.get(id);
    if (session === undefined || !isLive(session)) throw new Error(`Session ${id} does not exist`);
    return session;
  }

  async function applyTransition(id: string, to: SessionStatus): Promise<SessionRecord> {
    const session = await requireSession(id);
    const outcome = transitionSession(session.status, to, Date.now());
    const updated: SessionRecord = {
      ...session,
      status: outcome.status,
      // `transitionSession`'s `endedAt` is authoritative, including `null`
      // (e.g. `abandoned → active`, where the session is open again and must
      // stop reading as ended) — `??` would wrongly fall back to the old
      // `session.endedAt` in exactly that case, since `null` is nullish.
      endedAt: outcome.endedAt,
      // A session that stops being open has nothing left to rest for.
      activeRestTimer: outcome.status === 'active' || outcome.status === 'paused' ? session.activeRestTimer : null,
      // Same rule for a Hold countdown — a paused/closed session has nothing
      // actively counting down either.
      activeHoldTimer: outcome.status === 'active' || outcome.status === 'paused' ? (session.activeHoldTimer ?? null) : null,
    };
    await database.sessions.put(updated);
    return updated;
  }

  const repository: SessionRepository = {
    async startFromPlanDay(planDayId, options = {}): Promise<SessionRecord> {
      const source = await planRepository.getPlanDaySource(planDayId);
      const now = Date.now();
      const tzOffsetMin = options.tzOffsetMin ?? currentTzOffsetMin();
      const localDate = options.localDate ?? localDateFromEpoch(now, tzOffsetMin);

      const draft = materialiseSession(source, {
        newId: () => crypto.randomUUID(),
        startedAt: now,
        localDate,
        tzOffsetMin,
        ...(options.name !== undefined ? { name: options.name } : {}),
      });

      return await persistDraft(draft, draft.name);
    },

    async startFree(name = null): Promise<SessionRecord> {
      const now = Date.now();
      const tzOffsetMin = currentTzOffsetMin();
      const record: SessionRecord = {
        id: crypto.randomUUID(),
        name,
        status: 'active',
        startedAt: now,
        endedAt: null,
        localDate: localDateFromEpoch(now, tzOffsetMin),
        tzOffsetMin,
        planId: null,
        planDayId: null,
        planSnapshot: null,
        notes: null,
        activeRestTimer: null,
        activeHoldTimer: null,
      };
      // One empty block so "add exercise" mid-workout always has somewhere
      // to attach to — a free workout otherwise has no block for the first
      // exercise to join.
      const block: SessionBlockRecord = {
        id: crypto.randomUUID(),
        sessionId: record.id,
        orderIndex: 0,
        kind: 'single',
        restSeconds: null,
        sourcePlanBlockId: null,
      };
      await database.transaction('rw', [database.sessions, database.sessionBlocks], async () => {
        await database.sessions.add(record);
        await database.sessionBlocks.add(block);
      });
      return record;
    },

    async getOpenSession(): Promise<SessionRecord | undefined> {
      return await database.sessions.where('status').anyOf(['active', 'paused']).filter(isLive).first();
    },

    async getSession(id): Promise<SessionRecord | undefined> {
      const row = await database.sessions.get(id);
      return row === undefined || !isLive(row) ? undefined : row;
    },

    async pause(id): Promise<SessionRecord> {
      return await applyTransition(id, 'paused');
    },

    async resume(id): Promise<SessionRecord> {
      return await applyTransition(id, 'active');
    },

    async abandon(id): Promise<SessionRecord> {
      return await applyTransition(id, 'abandoned');
    },

    async complete(id): Promise<WorkoutSummary> {
      const session = await requireSession(id);
      const now = Date.now();

      const exercises = await database.sessionExercises.where('sessionId').equals(id).filter(isLive).toArray();

      await database.transaction(
        'rw',
        [...allSessionTables, database.personalRecords, database.cardioSegments],
        async () => {
          const outcome = transitionSession(session.status, 'completed', now);
          const nextSession: SessionRecord = {
            ...session,
            status: outcome.status,
            endedAt: outcome.endedAt,
            activeRestTimer: null,
            activeHoldTimer: null,
          };
          await database.sessions.put(nextSession);

          for (const exercise of exercises) {
            if (exercise.exerciseId === null) continue;

            const sets = (await database.sessionSets
              .where('sessionExerciseId')
              .equals(exercise.id)
              .filter(isLive)
              .toArray()) as LoggedSet[];
            const segments = (
              await database.cardioSegments.where('sessionExerciseId').equals(exercise.id).filter(isLive).toArray()
            ).map(toCardioSegment);

            const existing = await personalRecordsRepository.getExisting(exercise.exerciseId);
            const result = detectPersonalRecords({
              exerciseId: exercise.exerciseId,
              sessionId: id,
              modality: exercise.modality,
              sets,
              segments,
              existing,
              at: now,
            });

            if (result.achieved.length > 0) {
              await personalRecordsRepository.applyDetected(id, exercise.exerciseId, result);
            }
          }
        },
      );

      // Recomputed fresh rather than assembled from the transaction above —
      // this is the exact same path `getSummary()` uses on a later visit
      // (§49 of the approved plan), so "just finished" and "viewed after a
      // reload" can never disagree.
      return await repository.getSummary(id);
    },

    async getSummary(id): Promise<WorkoutSummary> {
      const session = await requireSession(id);
      const exercises = await database.sessionExercises.where('sessionId').equals(id).filter(isLive).toArray();

      let totalVolumeGramReps = 0;
      let totalSets = 0;
      let totalReps = 0;
      const progressionSuggestions: ProgressionSuggestionEntry[] = [];
      // Cardio Phase C6. Gathered across every exercise, then summarised once
      // — `summariseCardio` weights average heart rate by duration, which only
      // comes out right over the whole session rather than per exercise.
      const allSegments: CardioSegment[] = [];

      for (const exercise of exercises) {
        const sets = (await database.sessionSets
          .where('sessionExerciseId')
          .equals(exercise.id)
          .filter(isLive)
          .toArray()) as LoggedSet[];

        allSegments.push(
          ...(
            await database.cardioSegments.where('sessionExerciseId').equals(exercise.id).filter(isLive).toArray()
          ).map(toCardioSegment),
        );

        const volume = strengthVolume(sets);
        totalVolumeGramReps += volume.volumeGramReps;
        totalSets += sets.filter((s) => s.isCompleted).length;
        totalReps += volume.totalReps;

        if (exercise.exerciseId === null || exercise.sourcePlanExerciseId === null) continue;
        const rule = await planRepository.getProgressionRule(exercise.sourcePlanExerciseId);
        if (rule === undefined || !rule.enabled) continue;

        const snapshot = exercise.prescriptionSnapshot as { sets?: unknown } | null;
        const prescribedSets = Array.isArray(snapshot?.sets) ? snapshot.sets : [];
        const suggestion = suggestProgression({
          rule: {
            strategy: rule.strategy,
            incrementG: rule.incrementG ?? 0,
            roundingG: rule.roundingG ?? 500,
            enabled: rule.enabled,
          },
          prescribed: prescribedSets,
          lastPerformance: sets,
        });
        progressionSuggestions.push({ sessionExerciseId: exercise.id, exerciseId: exercise.exerciseId, suggestion });
      }

      const achievedRecords = await database.personalRecords.where('sessionId').equals(id).toArray();
      const durationS = Math.round(((session.endedAt ?? Date.now()) - session.startedAt) / 1000);

      return {
        session,
        stats: {
          totalVolumeGramReps,
          totalSets,
          totalReps,
          durationS,
          // `null` when the session has no segments at all, so a strength-only
          // workout's stats are exactly what they were before C6.
          cardio: allSegments.length === 0 ? null : summariseCardio(allSegments),
        },
        achievedRecords,
        progressionSuggestions,
      };
    },

    async deleteSession(id): Promise<void> {
      await integrityRepository.deleteSession(id);
      // Spans however many exercises the session touched — a full clear is
      // simpler and just as correct as resolving each one, and deleting an
      // entire session is rare enough that the extra recompute cost is fine.
      progressRepository.invalidate();
    },

    async listBlocks(sessionId): Promise<SessionBlockRecord[]> {
      const rows = await database.sessionBlocks.where('sessionId').equals(sessionId).filter(isLive).toArray();
      return rows.sort((a, b) => a.orderIndex - b.orderIndex);
    },

    async listExercises(sessionBlockId): Promise<SessionExerciseRecord[]> {
      const rows = await database.sessionExercises.where('sessionBlockId').equals(sessionBlockId).filter(isLive).toArray();
      return rows.sort((a, b) => a.orderIndex - b.orderIndex);
    },

    async listSets(sessionExerciseId): Promise<SessionSetRecord[]> {
      const rows = await database.sessionSets.where('sessionExerciseId').equals(sessionExerciseId).filter(isLive).toArray();
      return rows.sort((a, b) => a.setIndex - b.setIndex || a.dropIndex - b.dropIndex);
    },

    async listSegments(sessionExerciseId): Promise<CardioSegmentRecord[]> {
      const rows = await database.cardioSegments.where('sessionExerciseId').equals(sessionExerciseId).filter(isLive).toArray();
      return rows.sort((a, b) => a.orderIndex - b.orderIndex);
    },

    async completeSet(setId, values): Promise<SessionSetRecord> {
      const existing = await database.sessionSets.get(setId);
      if (existing === undefined || !isLive(existing)) throw new Error(`Set ${setId} does not exist`);
      await assertSessionExerciseMutable(existing.sessionExerciseId);

      const updated: SessionSetRecord = {
        ...existing,
        ...values,
        isCompleted: true,
        // Idempotent: re-completing an already-completed set keeps its
        // original completion instant rather than bumping it forward on a
        // duplicate tap (Phase 6 §41 — a double-tap must not look like two
        // performances of the same set).
        completedAt: existing.completedAt ?? Date.now(),
      };
      await database.sessionSets.put(updated);
      await invalidateProgressFor(existing.sessionExerciseId);
      return updated;
    },

    async uncompleteSet(setId): Promise<SessionSetRecord> {
      const existing = await database.sessionSets.get(setId);
      if (existing === undefined || !isLive(existing)) throw new Error(`Set ${setId} does not exist`);
      await assertSessionExerciseMutable(existing.sessionExerciseId);

      const updated: SessionSetRecord = { ...existing, isCompleted: false, completedAt: null };
      await database.sessionSets.put(updated);
      await invalidateProgressFor(existing.sessionExerciseId);
      return updated;
    },

    async addSet(sessionExerciseId): Promise<SessionSetRecord> {
      const existing = await database.sessionSets.where('sessionExerciseId').equals(sessionExerciseId).filter(isLive).toArray();
      const exercise = await database.sessionExercises.get(sessionExerciseId);
      if (exercise === undefined || !isLive(exercise)) throw new Error(`Session exercise ${sessionExerciseId} does not exist`);
      await assertSessionMutable(exercise.sessionId);

      // `buildAddedSet` only reads `dropIndex`/`setIndex`/load fields off the
      // existing sets, never `displayIndex` — it is present on the type
      // purely because `SessionSetDraft` extends it for render-time use, so
      // a placeholder here is safe. `displayIndex` itself is never stored
      // (Phase 0: "display order is computed at render time"), so the UI
      // recomputes it from `listSets()` via `resequenceDisplay` when needed,
      // not this repository.
      const draft = buildAddedSet(
        {
          id: sessionExerciseId,
          loadType: exercise.loadType,
          sets: existing.map((set) => ({ ...set, displayIndex: 0 })),
        },
        { id: crypto.randomUUID() },
      );
      const record: SessionSetRecord = { ...draft, notes: null };
      await database.sessionSets.add(record);
      await invalidateProgressFor(sessionExerciseId);
      return record;
    },

    async removeSet(setId): Promise<void> {
      const existing = await database.sessionSets.get(setId);
      if (existing === undefined || !isLive(existing)) return;
      await assertSessionExerciseMutable(existing.sessionExerciseId);

      // `setIndex` is identity, not position (Phase 0 §5.3) — deleting a set
      // never renumbers its siblings; display order is recomputed by the UI
      // at render time via `resequenceDisplay`, not persisted here.
      await tombstoneById(database.sessionSets, setId, Date.now());
      await invalidateProgressFor(existing.sessionExerciseId);
    },

    // ── Cardio segments (Cardio Phase C2) ────────────────────────────────
    //
    // Written against the four set mutators directly above, not against a
    // fresh design: same existence check, same mutability guard, same
    // tombstone-on-remove, same "index is identity, never renumbered" rule.
    //
    // Note what is deliberately ABSENT: no `updatedAt` is maintained, because
    // `CardioSegmentRecord` has no such field — and neither does
    // `SessionSetRecord`. Across this whole database only `exercises`,
    // `exerciseUserState` and `userMedia` carry `updatedAt`; every
    // session-family row leaves it to the server, which stamps `updated_at`
    // itself from its own clock on each push (`functions/api/sync/push.ts`).
    // Adding one here would both invent a field and diverge from sets.

    async addSegment(sessionExerciseId, values = {}): Promise<CardioSegmentRecord> {
      const exercise = await database.sessionExercises.get(sessionExerciseId);
      if (exercise === undefined || !isLive(exercise)) {
        throw new Error(`Session exercise ${sessionExerciseId} does not exist`);
      }
      await assertSessionMutable(exercise.sessionId);

      // Max + 1 over LIVE siblings — the identical expression `addExercise`
      // uses for its own `orderIndex`. Tombstoned rows are excluded, so a
      // removed segment's index is never reused; combined with the
      // no-renumbering rule below that keeps every index stable for the life
      // of the session.
      const existing = await database.cardioSegments
        .where('sessionExerciseId')
        .equals(sessionExerciseId)
        .filter(isLive)
        .toArray();
      const orderIndex = existing.reduce((max, row) => Math.max(max, row.orderIndex), -1) + 1;

      const record: CardioSegmentRecord = {
        id: crypto.randomUUID(),
        sessionExerciseId,
        orderIndex,
        durationS: values.durationS ?? null,
        distanceM: values.distanceM ?? null,
        avgHeartRate: values.avgHeartRate ?? null,
        resistanceLevel: values.resistanceLevel ?? null,
        estimatedSpeedKph: values.estimatedSpeedKph ?? null,
        inclinePct: values.inclinePct ?? null,
        // Starts incomplete, exactly as a materialised or added set does.
        // `CardioSegmentRecord` has no `completedAt`; `isCompleted` alone is
        // the whole completion state, and C2 does not add one.
        isCompleted: false,
      };
      await database.cardioSegments.add(record);
      await invalidateProgressFor(sessionExerciseId);
      return record;
    },

    async updateSegment(segmentId, values): Promise<CardioSegmentRecord> {
      const existing = await database.cardioSegments.get(segmentId);
      // The `!isLive` half is what stops a tombstoned segment being
      // resurrected by a late write — the same guard `completeSet` uses.
      if (existing === undefined || !isLive(existing)) {
        throw new Error(`Cardio segment ${segmentId} does not exist`);
      }
      await assertSessionExerciseMutable(existing.sessionExerciseId);

      // Spread `values` over the row, so identity (`id`, `sessionExerciseId`,
      // `orderIndex`) and completion state are preserved by construction: an
      // absent key cannot overwrite anything, only an explicit one can.
      const updated: CardioSegmentRecord = { ...existing, ...values };
      await database.cardioSegments.put(updated);
      await invalidateProgressFor(existing.sessionExerciseId);
      return updated;
    },

    async completeSegment(segmentId, values = {}): Promise<CardioSegmentRecord> {
      const existing = await database.cardioSegments.get(segmentId);
      if (existing === undefined || !isLive(existing)) {
        throw new Error(`Cardio segment ${segmentId} does not exist`);
      }
      await assertSessionExerciseMutable(existing.sessionExerciseId);

      // Naturally idempotent: unlike `completeSet` there is no `completedAt`
      // instant that a second tap could push forward, so re-completing is a
      // no-op beyond re-writing the same values.
      const updated: CardioSegmentRecord = { ...existing, ...values, isCompleted: true };
      await database.cardioSegments.put(updated);
      await invalidateProgressFor(existing.sessionExerciseId);
      return updated;
    },

    async uncompleteSegment(segmentId): Promise<CardioSegmentRecord> {
      const existing = await database.cardioSegments.get(segmentId);
      if (existing === undefined || !isLive(existing)) {
        throw new Error(`Cardio segment ${segmentId} does not exist`);
      }
      await assertSessionExerciseMutable(existing.sessionExerciseId);

      // Logged values are kept, matching `uncompleteSet`: unticking a segment
      // corrects a mistaken completion, it does not discard what was entered.
      const updated: CardioSegmentRecord = { ...existing, isCompleted: false };
      await database.cardioSegments.put(updated);
      await invalidateProgressFor(existing.sessionExerciseId);
      return updated;
    },

    async removeSegment(segmentId): Promise<void> {
      const existing = await database.cardioSegments.get(segmentId);
      // Returns silently rather than throwing, exactly as `removeSet` does,
      // so removing an already-removed segment is safe.
      if (existing === undefined || !isLive(existing)) return;
      await assertSessionExerciseMutable(existing.sessionExerciseId);

      // `orderIndex` is identity, not position: removing the middle segment
      // of three leaves 0, 2 and the next append becomes 3. Renumbering would
      // rewrite rows the user did not touch and force needless sync traffic.
      await tombstoneById(database.cardioSegments, segmentId, Date.now());
      await invalidateProgressFor(existing.sessionExerciseId);
    },

    async addExercise(sessionBlockId, exerciseId): Promise<SessionExerciseRecord> {
      const exerciseRow = await exerciseRepository.get(exerciseId);
      if (exerciseRow === undefined) throw new Error(`Exercise ${exerciseId} does not exist`);
      const block = await database.sessionBlocks.get(sessionBlockId);
      if (block === undefined || !isLive(block)) throw new Error(`Session block ${sessionBlockId} does not exist`);
      await assertSessionMutable(block.sessionId);

      const existing = await database.sessionExercises.where('sessionBlockId').equals(sessionBlockId).filter(isLive).toArray();
      const orderIndex = existing.reduce((max, row) => Math.max(max, row.orderIndex), -1) + 1;

      const record: SessionExerciseRecord = {
        id: crypto.randomUUID(),
        sessionBlockId,
        sessionId: block.sessionId,
        exerciseId,
        exerciseNameSnapshot: exerciseRow.name,
        mediaIdsSnapshot: exerciseRow.mediaIds.length > 0 ? exerciseRow.mediaIds : null,
        orderIndex,
        status: 'pending',
        modality: exerciseRow.modality,
        loadType: exerciseRow.loadType,
        restSeconds: null,
        notes: null,
        sourcePlanExerciseId: null,
        prescriptionSnapshot: null,
      };
      await database.sessionExercises.add(record);
      return record;
    },

    async removeExercise(sessionExerciseId): Promise<void> {
      const existing = await database.sessionExercises.get(sessionExerciseId);
      if (existing === undefined || !isLive(existing)) return;
      await assertSessionMutable(existing.sessionId);

      // Before the tombstone, while the exercise still resolves to its
      // library `exerciseId` — trends include open sessions, so a removed
      // exercise's in-progress sets must stop counting immediately.
      await invalidateProgressFor(sessionExerciseId);

      await database.transaction(
        'rw',
        [database.sessions, database.sessionExercises, database.sessionSets, database.cardioSegments],
        async () => {
          const now = Date.now();
          const allSets = await database.sessionSets.where('sessionExerciseId').equals(sessionExerciseId).toArray();
          const allSegments = await database.cardioSegments.where('sessionExerciseId').equals(sessionExerciseId).toArray();

          // Only LIVE children are stamped: a set removed earlier keeps its
          // original `deletedAt` rather than being re-dated by this removal.
          await tombstoneRows(database.sessionSets, allSets.filter(isLive), now);
          await tombstoneRows(database.cardioSegments, allSegments.filter(isLive), now);
          await tombstoneById(database.sessionExercises, sessionExerciseId, now);

          // A Hold countdown pointing at one of these sets would reference a
          // tombstoned row. The rest timer is block-scoped and the block
          // survives, so it is deliberately left alone.
          const session = await database.sessions.get(existing.sessionId);
          const timer = session?.activeHoldTimer ?? null;
          if (timer !== null && allSets.some((set) => set.id === timer.sessionSetId)) {
            await database.sessions.update(existing.sessionId, { activeHoldTimer: null });
          }
        },
      );
    },

    async skipExercise(sessionExerciseId): Promise<SessionExerciseRecord> {
      const existing = await database.sessionExercises.get(sessionExerciseId);
      if (existing === undefined || !isLive(existing)) throw new Error(`Session exercise ${sessionExerciseId} does not exist`);
      await assertSessionMutable(existing.sessionId);

      const updated: SessionExerciseRecord = { ...existing, status: 'skipped' };
      await database.sessionExercises.put(updated);
      return updated;
    },

    async unskipExercise(sessionExerciseId): Promise<SessionExerciseRecord> {
      const existing = await database.sessionExercises.get(sessionExerciseId);
      if (existing === undefined || !isLive(existing)) throw new Error(`Session exercise ${sessionExerciseId} does not exist`);
      await assertSessionMutable(existing.sessionId);

      const updated: SessionExerciseRecord = { ...existing, status: 'pending' };
      await database.sessionExercises.put(updated);
      return updated;
    },

    async updateSessionNotes(sessionId, notes): Promise<void> {
      await database.sessions.update(sessionId, { notes });
    },

    async updateSetNotes(setId, notes): Promise<void> {
      const existing = await database.sessionSets.get(setId);
      if (existing !== undefined) await assertSessionExerciseMutable(existing.sessionExerciseId);
      await database.sessionSets.update(setId, { notes });
    },

    async startRestTimer(sessionId, sessionBlockId, durationS): Promise<ActiveRestTimer> {
      const now = Date.now();
      const timer: ActiveRestTimer = { startedAt: now, endsAt: now + Math.round(durationS * 1000), sessionBlockId };
      await database.sessions.update(sessionId, { activeRestTimer: timer });
      return timer;
    },

    async adjustRestTimer(sessionId, deltaS): Promise<ActiveRestTimer | null> {
      const session = await requireSession(sessionId);
      if (session.activeRestTimer === null) return null;

      const endsAt = Math.max(session.activeRestTimer.startedAt, session.activeRestTimer.endsAt + Math.round(deltaS * 1000));
      const timer: ActiveRestTimer = { ...session.activeRestTimer, endsAt };
      await database.sessions.update(sessionId, { activeRestTimer: timer });
      return timer;
    },

    async skipRestTimer(sessionId): Promise<void> {
      await database.sessions.update(sessionId, { activeRestTimer: null });
    },

    /**
     * Starts (or restarts) the session's one Hold countdown, on `sessionSetId`
     * — Plank / Hold Exercise Countdown Timer feature. Unconditionally
     * overwrites whatever was in the slot before, which is exactly how a
     * previous set's stale timer is prevented from lingering: starting a new
     * one on a different set simply replaces it, the same single-slot
     * precedent `startRestTimer` already establishes for rest.
     */
    async startHoldTimer(sessionId, sessionSetId, durationS): Promise<ActiveHoldTimer> {
      const now = Date.now();
      const timer: ActiveHoldTimer = { startedAt: now, endsAt: now + Math.round(durationS * 1000), sessionSetId };
      await database.sessions.update(sessionId, { activeHoldTimer: timer });
      return timer;
    },

    /** Clears the active Hold countdown, if any — used both for an explicit
     *  Reset/Stop and once the set it belongs to is completed. */
    async clearHoldTimer(sessionId): Promise<void> {
      await database.sessions.update(sessionId, { activeHoldTimer: null });
    },

    async getPreviousPerformance(exerciseId, excludeSessionId): Promise<PreviousPerformance | null> {
      const sessionExercises = await database.sessionExercises.where('exerciseId').equals(exerciseId).filter(isLive).toArray();

      const history: PerformedExercise[] = [];
      for (const exercise of sessionExercises) {
        const session = await database.sessions.get(exercise.sessionId);
        if (session === undefined || !isLive(session)) continue;

        const sets = (await database.sessionSets
          .where('sessionExerciseId')
          .equals(exercise.id)
          .filter(isLive)
          .toArray()) as LoggedSet[];

        history.push({
          sessionExerciseId: exercise.id,
          sessionId: exercise.sessionId,
          exerciseId,
          modality: exercise.modality,
          sessionStartedAt: session.startedAt,
          sets,
        });
      }

      return selectPreviousPerformance({ history, excludeSessionId });
    },

    prefillFor(previous, setIndex): ReturnType<typeof prefillForSet> {
      return prefillForSet(previous, setIndex);
    },
  };

  return repository;
}

export const sessionRepository = createSessionRepository();
